"use client";

import { useEffect } from "react";

// The only client-side code of the docs: copy buttons on code blocks and the "on this page" highlight.
// Without JavaScript the copy buttons stay hidden and the table of contents is a plain list of links.
export default function Enhance() {
  useEffect(() => {
    document.documentElement.dataset.qpJs = "1";

    const onClick = async (e: MouseEvent) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-copy]");
      if (!btn) return;
      const pre = btn.closest(".code-block")?.querySelector("pre");
      if (!pre) return;
      const text = btn.querySelector<HTMLElement>(".code-copy-text");
      try {
        await navigator.clipboard.writeText(pre.innerText.replace(/\n$/, ""));
        btn.dataset.state = "copied";
        btn.setAttribute("aria-label", "Copied");
      } catch {
        if (text) text.textContent = "press ctrl+c";
        const range = document.createRange();
        range.selectNodeContents(pre);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
      window.setTimeout(() => {
        delete btn.dataset.state;
        btn.setAttribute("aria-label", "Copy code");
        if (text) text.textContent = "";
      }, 1600);
    };
    document.addEventListener("click", onClick);

    const links = Array.from(document.querySelectorAll<HTMLAnchorElement>(".qp-toc a[href^='#']"));
    const targets = links
      .map((a) => document.getElementById(decodeURIComponent(a.hash.slice(1))))
      .filter((el): el is HTMLElement => !!el);
    let observer: IntersectionObserver | undefined;
    if (targets.length && "IntersectionObserver" in window) {
      const visible = new Set<string>();
      const mark = () => {
        // the first heading in view, or else the last one above the viewport
        let id = targets.find((t) => visible.has(t.id))?.id;
        if (!id) id = [...targets].reverse().find((t) => t.getBoundingClientRect().top < 0)?.id ?? targets[0].id;
        for (const a of links) a.toggleAttribute("data-active", a.hash === `#${encodeURIComponent(id)}` || a.hash === `#${id}`);
      };
      observer = new IntersectionObserver(
        (entries) => {
          for (const en of entries) {
            if (en.isIntersecting) visible.add(en.target.id);
            else visible.delete(en.target.id);
          }
          mark();
        },
        { rootMargin: "0px 0px -65% 0px" },
      );
      targets.forEach((t) => observer!.observe(t));
    }

    return () => {
      document.removeEventListener("click", onClick);
      observer?.disconnect();
    };
  }, []);
  return null;
}
